/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { service } from '@ember/service';
import { StrokeOptions, EflexObjTypes } from 'eflex/constants/work-instructions/tool-props';
import { generateStrokeDashArray } from 'eflex/util/stroke-helper';
import { all, task, waitForProperty, waitForQueue, timeout } from 'ember-concurrency';
import * as Fabric from 'fabric';
import { updateObjectProperty, getArrowsByLine } from 'eflex/util/fabric-helpers';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { waitFor } from '@ember/test-waiters';
import getDelayTime from 'eflex/util/get-delay-time';

export default class WorkInstructionEditorToolPropertiesLines extends Component {
  @service imageEditor;
  @service systemConfig;
  @service store;

  @tracked selectedLineType = EflexObjTypes.STRAIGHT_LINE;
  @tracked selectedStrokeColor = '#49fb35';
  @tracked startArrow = false;
  @tracked endArrow = false;
  @tracked currentLine;
  @tracked startHidden = false;
  @tracked dynamicObjectName;
  @tracked selectedStrokeWidth = 3;
  @tracked selectedStrokeStyle = StrokeOptions.default;
  @tracked strokeDashArray = [];

  #hasListeners = false;

  get lineTypes() {
    return [
      { label: 'imageEditor.lines.straight', value: EflexObjTypes.STRAIGHT_LINE },
      { label: 'imageEditor.lines.freeform', value: EflexObjTypes.FREE_LINE },
    ];
  }

  onDidInsert = task(waitFor(async () => {
    await all([
      waitForProperty(this.imageEditor, 'canvas'),
      waitForQueue('afterRender'),
    ]);

    if (this._activeObject() != null) {
      this.updateSelectedProperties();
    } else {
      this.#updateFromSystemConfig();
    }

    this.imageEditor
      .on('selection:updated', this.updateSelectedProperties)
      .on('selection:cleared', this.#updateFromSystemConfig);

    this.#hasListeners = true;

    const canvas = this.imageEditor.canvas;

    canvas.on('mouse:down', this.#placeLine);
    canvas.on('mouse:move', this.#adjustLine);
    canvas.on('mouse:up', this.#releaseLine);
    canvas.on('path:created', this.#pathCreated);
  }));

  _isLine(obj) {
    return this.lineTypes.map(item => item.value).includes(obj?.eflex?.type);
  }

  _isLineValid(line) {
    return !(line.x1 === line.x2 && line.y1 === line.y2);
  }

  _activeObject() {
    const obj = this.imageEditor.canvas?.getActiveObject();
    if (this._isLine(obj)) {
      return obj;
    } else {
      return null;
    }
  }

  resetProperties() {
    this.link = '';
  }

  #updateFromSystemConfig = (event) => {
    this.resetProperties();
    const config = this.systemConfig.getWieDefaults('lines');

    Object.assign(this, {
      selectedLineType: config.type,

      startArrow: config.arrows?.start,
      endArrow: config.arrows?.end,

      selectedStrokeColor: config.stroke?.color,
      strokeDashArray: generateStrokeDashArray(config.stroke?.style, config.stroke?.width),
      selectedStrokeWidth: config.stroke?.width,
      selectedStrokeStyle: config.stroke?.style,
    });

    if (event == null) {
      this.imageEditor.canvas.set('isDrawingMode', this._isDrawingMode());
    }

    this.#updateDrawingTool();
  };

  @action
  updateSelectedProperties() {
    const obj = this._activeObject();

    if (obj == null) {
      return;
    }

    Object.assign(this, {
      startArrow: this._arrow(obj, 'start')?.visible,
      endArrow: this._arrow(obj, 'end')?.visible,
      selectedStrokeColor: obj.stroke,
      selectedStrokeWidth: obj.strokeWidth,
      strokeDashArray: obj.strokeDashArray,
      selectedStrokeStyle: obj.eflex?.strokeDashArrayStyle ?? StrokeOptions.default,
      selectedLineType: obj.eflex?.type,
      dynamicObjectName: obj.eflex?.dynamicObjectName,
      startHidden: obj.eflex?.startHidden,
    });

    this.#updateDrawingTool();
  }

  #updateArrowStyle() {
    const line = this._activeObject();
    if (!line) {
      return;
    }
    this._arrow(line).forEach((arrow) => {
      if (!arrow) {
        return;
      }
      arrow.eflex ??= {};
      updateObjectProperty(arrow.eflex, 'startHidden', this.startHidden);
      updateObjectProperty(arrow, 'strokeWidth', this.selectedStrokeWidth);
      updateObjectProperty(arrow, 'stroke', this.selectedStrokeColor);
      updateObjectProperty(arrow, 'fill', this.selectedStrokeColor);
      this.imageEditor.notifyModified(arrow);
    });
  }

  #updateDrawingTool() {
    Object.assign(this.imageEditor.canvas.freeDrawingBrush, {
      width: this.selectedStrokeWidth,
      color: this.selectedStrokeColor,
      strokeDashArray: this.strokeDashArray,
    });
  }

  _addStrokeStyle(path) {
    path.eflex ??= {};
    updateObjectProperty(path.eflex, 'strokeDashArrayStyle', this.selectedStrokeStyle);
    this.imageEditor.notifyModified(path);
  }

  _isDrawingMode() {
    return this.selectedLineType === EflexObjTypes.FREE_LINE;
  }

  _setActiveFromHandle() {
    const { canvas } = this.imageEditor;
    const line = this.imageEditor.getLineByHandle(canvas.getActiveObject());

    if (line) {
      canvas.setActiveObject(line);
    }
  }

  #placeLine = ({ e }) => {
    const { canvas } = this.imageEditor;

    if (!this.imageEditor.inToolMode || canvas.isDrawingMode || canvas.getActiveObject() != null) {
      this.currentLine = null;
      return;
    }

    const pointer = canvas.getScenePoint(e);
    const points = [pointer.x, pointer.y, pointer.x, pointer.y];

    const line = new Fabric.Line(points, {
      originX: 'center',
      originY: 'center',
      strokeWidth: Number.parseInt(this.selectedStrokeWidth),
      stroke: this.selectedStrokeColor,
      strokeDashArray: this.strokeDashArray,
      selectable: false,
      hasControls: false,
      hasBorders: false,
      perPixelTargetFind: true,
      eflex: {
        strokeDashArrayStyle: this.selectedStrokeStyle,
        type: EflexObjTypes.STRAIGHT_LINE,
        dynamicObjectName: this.dynamicObjectName,
        startHidden: this.startHidden,
      },
    });

    this.imageEditor.applyItemKey(line);
    this.currentLine = line;
    this.imageEditor.disableHistory();
    canvas.add(line);
  };

  #adjustLine = ({ e }) => {
    const { canvas } = this.imageEditor;

    if (this.currentLine == null) {
      return;
    }
    const pointer = canvas.getScenePoint(e);

    this.currentLine.set({ x2: pointer.x, y2: pointer.y });
    canvas.renderAll();
  };

  #releaseLine = () => {
    if (this.currentLine == null) {
      this._setActiveFromHandle();
      return;
    }

    const { canvas } = this.imageEditor;
    this.currentLine.setCoords();

    if (!this._isLineValid(this.currentLine)) {
      this.imageEditor.removeObject(this.currentLine, true);
      this.currentLine = null;
      this.imageEditor.enableHistory();
      return;
    }

    this._addStrokeStyle(this.currentLine);
    let startArrow = null;
    let endArrow = null;

    const angle = this.imageEditor.calcLineAngle(
      this.currentLine.x1,
      this.currentLine.y1,
      this.currentLine.x2,
      this.currentLine.y2,
    );

    startArrow = this.imageEditor.createArrow({
      x: this.currentLine.x1,
      y: this.currentLine.y1,
      angle: angle - 180,
      itemKey: this.currentLine.eflex.itemKey,
      fill: this.selectedStrokeColor,
      stroke: this.selectedStrokeColor,
      strokeWidth: Number.parseInt(this.selectedStrokeWidth),
      itemPosition: 'start',
      visible: this.startArrow,
    });

    endArrow = this.imageEditor.createArrow({
      x: this.currentLine.x2,
      y: this.currentLine.y2,
      angle,
      itemKey: this.currentLine.eflex.itemKey,
      fill: this.selectedStrokeColor,
      stroke: this.selectedStrokeColor,
      strokeWidth: Number.parseInt(this.selectedStrokeWidth),
      itemPosition: 'end',
      visible: this.endArrow,
    });

    const startHandle = this.imageEditor.createHandle({
      x: this.currentLine.x1,
      y: this.currentLine.y1,
      itemKey: this.currentLine.eflex.itemKey,
      itemPosition: 'start',
    });

    const endHandle = this.imageEditor.createHandle({
      x: this.currentLine.x2,
      y: this.currentLine.y2,
      itemKey: this.currentLine.eflex.itemKey,
      itemPosition: 'end',
    });

    canvas.add(startArrow, endArrow, startHandle, endHandle);

    canvas.setActiveObject(this.currentLine);
    this.#updateArrowStyle();
    canvas.renderAll();

    this.imageEditor.enableHistory();
    this.imageEditor.updateLineItemsPosition(this.currentLine);
    canvas.fire('object:modified', { target: this.currentLine });

    this.args.objectCreated?.(this.currentLine);
    this.currentLine = null;
  };

  #pathCreated = ({ path }) => {
    const { canvas } = this.imageEditor;
    path.eflex ??= {};
    path.setCoords();
    this._addStrokeStyle(path);
    updateObjectProperty(path.eflex, 'type', EflexObjTypes.FREE_LINE);
    this.imageEditor.notifyModified(path);
    canvas.set('isDrawingMode', false);
    canvas.renderAll();

    this.args.objectCreated?.(path);
  };

  _arrow(line, position) {
    const arrows = getArrowsByLine(this.imageEditor.canvas, line, position);

    if (position != null) {
      if (arrows.length > 1) {
        throw new Error('More arrows than expected for line.');
      }
      return arrows[0];
    } else {
      return arrows;
    }
  }

  #updateArrowVisibility(line, position, visible) {
    const arrow = this._arrow(line, position);
    updateObjectProperty(arrow, 'visible', visible);
    this.imageEditor.notifyModified(arrow);
  }

  willDestroy() {
    super.willDestroy(...arguments);
    const imageEditor = this.imageEditor;

    if (this.#hasListeners) {
      imageEditor
        .off('selection:updated', this.updateSelectedProperties)
        .off('selection:cleared', this.#updateFromSystemConfig);
    }

    const canvas = imageEditor.canvas;

    if (canvas) {
      [
        'mouse:down',
        'mouse:move',
        'mouse:up',
        'path:created',
      ].forEach(removedEvent => canvas.off(removedEvent));
    }
  }

  #updateDrawingToolAndArrows() {
    this.#updateDrawingTool();
    this.#updateArrowStyle();
  }

  @action
  updateDynamicObjectName(val) {
    this.dynamicObjectName = val;
    this.#updateDrawingToolAndArrows();
    const obj = this._activeObject();
    if (!obj) {
      return;
    }
    obj.eflex ??= {};
    updateObjectProperty(obj.eflex, 'dynamicObjectName', val);
    this.imageEditor.notifyModified(obj);
  }

  @action
  updateStartHidden(val) {
    this.startHidden = val;
    this.#updateDrawingToolAndArrows();
    const obj = this._activeObject();
    if (!obj) {
      return;
    }
    obj.eflex ??= {};
    updateObjectProperty(obj.eflex, 'startHidden', val);
    this.imageEditor.notifyModified(obj);
  }

  onUpdateStrokeColor = task({ restartable: true }, waitFor(async (val) => {
    this.selectedStrokeColor = val;

    await timeout(getDelayTime(50));

    const obj = this._activeObject();

    this.#updateDrawingToolAndArrows();
    updateObjectProperty(obj, 'stroke', val);
    this.imageEditor.notifyModified(obj);
  }));

  @action
  updateStroke(strokeDashArray, selectedStrokeStyle, selectedStrokeWidth) {
    Object.assign(this, {
      strokeDashArray,
      selectedStrokeStyle,
      selectedStrokeWidth,
    });

    this.#updateDrawingToolAndArrows();
    const obj = this._activeObject();

    if (!obj) {
      return;
    }

    obj.eflex ??= {};
    updateObjectProperty(obj, 'strokeWidth', selectedStrokeWidth);
    updateObjectProperty(obj.eflex, 'strokeDashArrayStyle', selectedStrokeStyle);
    updateObjectProperty(obj, 'strokeDashArray', strokeDashArray);
    this.imageEditor.notifyModified(obj);
  }

  @action
  switchLineType(type) {
    this.selectedLineType = type;
    this.imageEditor.canvas.set('isDrawingMode', this._isDrawingMode());
  }

  @action
  updateArrow(position, value) {
    const itemPosition = `${position}Arrow`;
    this[itemPosition] = value;

    const line = this._activeObject();
    if (line == null) {
      return;
    }
    this.#updateArrowVisibility(line, position, this[itemPosition]);
  }

  @action
  onMakeDefault() {
    const lineDefault = this.store.createRecord('wie-config/lines', {
      type: this.selectedLineType,
      stroke: this.store.createRecord('wie-config/stroke', {
        color: this.selectedStrokeColor,
        style: this.selectedStrokeStyle,
        width: this.selectedStrokeWidth,
      }),
      arrows: this.store.createRecord('wie-config/arrows', {
        start: this.startArrow,
        end: this.endArrow,
      }),
    });

    this.systemConfig.config.wie.editorDefaults.lines = lineDefault;
    return this.args.saveDefault();
  }
}
